import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "./products.css";

const CATEGORY_ICON = { "Запчасти": "fa-wrench" };
const iconFor = (cat) => CATEGORY_ICON[cat] || "fa-box";

const statusOf = (item) => {
    if (item.qty <= 0) return { cls: "pr-bad", text: "Нет в наличии" };
    if (item.qty <= item.minQty) return { cls: "pr-warn", text: "Заканчивается" };
    return { cls: "pr-ok", text: "В наличии" };
};

const Products = ({ API_URL }) => {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]); // [{id, name, isProtected, itemsCount}]
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("Все");
    const [openHistoryId, setOpenHistoryId] = useState(null);

    const [showAddModal, setShowAddModal] = useState(false);
    const [addKind, setAddKind] = useState(null); // 'Товар' | 'Запчасть'
    const [addForm, setAddForm] = useState({ name: "", category: "", unit: "шт", qty: "" });

    const [actionItem, setActionItem] = useState(null);
    const [actionType, setActionType] = useState(null);
    const [actionQty, setActionQty] = useState("");
    const [actionNote, setActionNote] = useState("");

    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [allItemsForHistory, setAllItemsForHistory] = useState([]); // включая удалённые — только для "Общей истории"

    const [editItemId, setEditItemId] = useState(null);
    const [editItemName, setEditItemName] = useState("");

    // ── 5 кликов на "Склад" — управление категориями и товарами ──
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState("");
    const titleClickCount = useRef(0);
    const titleClickTimer = useRef(null);
    const handleTitleClick = () => {
        titleClickCount.current += 1;
        if (titleClickTimer.current) clearTimeout(titleClickTimer.current);
        if (titleClickCount.current >= 5) {
            titleClickCount.current = 0;
            setNewCategoryName("");
            setShowCategoryModal(true);
            return;
        }
        titleClickTimer.current = setTimeout(() => { titleClickCount.current = 0; }, 1500);
    };

    const fetchItems = useCallback(() => {
        setLoading(true);
        axios.post(`${API_URL}/getInventoryItems`)
            .then((res) => { if (res.data.status === "success") setItems(res.data.items); })
            .catch(() => toast.error("Не удалось загрузить склад"))
            .finally(() => setLoading(false));
    }, [API_URL]);

    const fetchCategories = useCallback(() => {
        axios.post(`${API_URL}/getInventoryCategories`)
            .then((res) => { if (res.data.status === "success") setCategories(res.data.categories); })
            .catch(() => toast.error("Не удалось загрузить категории"));
    }, [API_URL]);

    const fetchAllItemsForHistory = useCallback(() => {
        axios.post(`${API_URL}/getInventoryItems`, { includeDeleted: true })
            .then((res) => { if (res.data.status === "success") setAllItemsForHistory(res.data.items); })
            .catch(() => toast.error("Не удалось загрузить историю"));
    }, [API_URL]);

    useEffect(() => { fetchItems(); fetchCategories(); }, [fetchItems, fetchCategories]);

    const categoryNames = ["Все", ...categories.map((c) => c.name)];

    const q = search.trim().toLowerCase();
    const filteredItems = useMemo(() => items
        .filter((i) => categoryFilter === "Все" || i.category === categoryFilter)
        .filter((i) => !q || i.name.toLowerCase().includes(q)), [items, categoryFilter, q]);

    const totalItems = items.length;
    const totalCategories = categories.length;
    const outOfStock = items.filter((i) => i.qty <= 0).length;

    // список категорий для выбора при добавлении обычного товара (без "Запчасти" — та выбирается через тип)
    const pickableCategories = categories.filter((c) => !c.isProtected);

    const openAddModal = () => {
        setAddKind(null);
        setAddForm({ name: "", category: pickableCategories[0]?.name || "", unit: "шт", qty: "" });
        setShowAddModal(true);
    };

    const submitAdd = () => {
        if (!addForm.name.trim()) { toast.error("Укажите наименование"); return; }
        const category = addKind === "Запчасть" ? "Запчасти" : (addForm.category || "Товары");
        axios.post(`${API_URL}/addInventoryItem`, {
            name: addForm.name.trim(), category, unit: addForm.unit.trim() || "шт", qty: Number(addForm.qty) || 0,
        }).then((res) => {
            if (res.data.status === "success") {
                toast.success("Позиция добавлена");
                setShowAddModal(false);
                fetchItems();
                fetchCategories();
            } else toast.error(res.data.message || "Ошибка");
        }).catch(() => toast.error("Ошибка сервера"));
    };

    const openAction = (item, type) => { setActionItem(item); setActionType(type); setActionQty(""); setActionNote(""); };

    const submitAction = () => {
        const qty = Number(actionQty);
        if (!qty) { toast.error("Укажите количество"); return; }
        const url = actionType === "income" ? "inventoryIncome" : "inventoryIssue";
        axios.post(`${API_URL}/${url}`, { itemId: actionItem.id, qty, note: actionNote })
            .then((res) => {
                if (res.data.status === "success") {
                    toast.success(actionType === "income" ? "Приход добавлен" : "Списание проведено");
                    setActionItem(null);
                    fetchItems();
                } else toast.error(res.data.message || "Ошибка");
            }).catch(() => toast.error("Ошибка сервера"));
    };

    const submitAddCategory = () => {
        if (!newCategoryName.trim()) { toast.error("Укажите название категории"); return; }
        axios.post(`${API_URL}/addInventoryCategory`, { name: newCategoryName.trim() })
            .then((res) => {
                if (res.data.status === "success") { toast.success("Категория добавлена"); setNewCategoryName(""); fetchCategories(); }
                else toast.error(res.data.message || "Ошибка");
            }).catch(() => toast.error("Ошибка сервера"));
    };

    const deleteCategory = (cat) => {
        if (!window.confirm(`Удалить категорию «${cat.name}»?`)) return;
        axios.post(`${API_URL}/deleteInventoryCategory`, { id: cat.id })
            .then((res) => {
                if (res.data.status === "success") { toast.success("Категория удалена"); fetchCategories(); }
                else toast.error(res.data.message || "Ошибка");
            }).catch(() => toast.error("Ошибка сервера"));
    };

    const startEditItem = (item) => { setEditItemId(item.id); setEditItemName(item.name); };

    const saveEditItem = () => {
        if (!editItemName.trim()) { toast.error("Укажите название"); return; }
        axios.post(`${API_URL}/renameInventoryItem`, { id: editItemId, name: editItemName.trim() })
            .then((res) => {
                if (res.data.status === "success") { toast.success("Товар переименован"); setEditItemId(null); fetchItems(); }
                else toast.error(res.data.message || "Ошибка");
            }).catch(() => toast.error("Ошибка сервера"));
    };

    const deleteItem = (item) => {
        if (!window.confirm(`Удалить «${item.name}» со склада? История операций по нему сохранится и останется видна в «Общей истории».`)) return;
        axios.post(`${API_URL}/deleteInventoryItem`, { id: item.id })
            .then((res) => {
                if (res.data.status === "success") { toast.success("Товар удалён, история сохранена"); fetchItems(); fetchCategories(); }
                else toast.error(res.data.message || "Ошибка");
            }).catch(() => toast.error("Ошибка сервера"));
    };

    const openHistoryModal = () => {
        fetchAllItemsForHistory();
        setShowHistoryModal(true);
    };

    return (
        <div className="pr-page">
            <div className="pr-topbar">
                <div className="pr-search">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input placeholder="Поиск по названию" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="pr-pagetitle" onClick={handleTitleClick} style={{ cursor: "default", userSelect: "none" }}>Склад</div>
                <div className="pr-actions">
                    <button className="pr-btn-outline" onClick={openHistoryModal}>
                        <i className="fa-solid fa-clock-rotate-left" /> Общая история
                    </button>
                    <button className="pr-btn-primary" onClick={openAddModal}>
                        <i className="fa-solid fa-plus" /> Добавить товар
                    </button>
                </div>
            </div>

            <div className="pr-stats">
                <div className="pr-stat"><div className="pr-lbl">Позиций на складе</div><div className="pr-val">{totalItems}</div></div>
                <div className="pr-stat"><div className="pr-lbl">Категорий</div><div className="pr-val">{totalCategories}</div></div>
                <div className="pr-stat pr-stat-debt"><div className="pr-lbl">Нет в наличии</div><div className="pr-val">{outOfStock}</div></div>
            </div>

            <div className="pr-chip-row">
                {categoryNames.map((c) => (
                    <span key={c} className={"pr-chip" + (categoryFilter === c ? " pr-chip-active" : "")} onClick={() => setCategoryFilter(c)}>
                        {c !== "Все" && <span className="pr-chip-dot" />}{c}
                    </span>
                ))}
            </div>

            {loading ? (
                <div className="pr-empty">Загрузка…</div>
            ) : !filteredItems.length ? (
                <div className="pr-empty">Ничего не найдено.</div>
            ) : (
                filteredItems.map((item) => {
                    const st = statusOf(item);
                    const isOpen = openHistoryId === item.id;
                    return (
                        <div className={"pr-item" + (item.qty <= 0 ? " pr-item-out" : "")} key={item.id}>
                            <div className="pr-item-row">
                                <div className={"pr-badge" + (item.category === "Запчасти" ? " pr-badge-part" : "")}>
                                    <i className={"fa-solid " + iconFor(item.category)} />
                                </div>
                                <div className="pr-item-info">
                                    <div className="pr-item-name">{item.name}</div>
                                    <div className="pr-item-sub">{item.category}</div>
                                </div>
                                <div className="pr-item-metric">
                                    <div className="pr-m-lbl">Остаток</div>
                                    <div className="pr-m-val">{item.qty <= 0 ? "0" : item.qty} {item.unit}</div>
                                </div>
                                <span className={"pr-badge-status " + st.cls}>{st.text}</span>
                                <div className="pr-item-actions">
                                    <button className="pr-btn-mini" onClick={() => openAction(item, "income")}>
                                        <i className="fa-solid fa-arrow-down" /> Приход
                                    </button>
                                    <button className="pr-btn-mini pr-btn-mini-red" onClick={() => openAction(item, "issue")}>
                                        <i className="fa-solid fa-arrow-up" /> Списание
                                    </button>
                                    <button className="pr-btn-mini" onClick={() => setOpenHistoryId(isOpen ? null : item.id)}>
                                        <i className={"fa-solid " + (isOpen ? "fa-chevron-up" : "fa-clock-rotate-left")} /> {isOpen ? "Скрыть" : "История"}
                                    </button>
                                </div>
                            </div>
                            {isOpen && (
                                <div className="pr-history">
                                    {item.history.length ? item.history.map((h) => (
                                        <div className="pr-history-row" key={h.id}>
                                            <span className="pr-history-desc">{h.date} · {h.note}</span>
                                            <span className={"pr-history-amt " + (h.type === "приход" ? "pr-amt-in" : "pr-amt-out")}>
                                                {h.type === "приход" ? "+" : "−"}{h.qty} {item.unit}
                                            </span>
                                        </div>
                                    )) : <div className="pr-history-empty">Пока нет операций</div>}
                                </div>
                            )}
                        </div>
                    );
                })
            )}

            {actionItem && (
                <div className="pr-modal-overlay" onClick={() => setActionItem(null)}>
                    <div className="pr-modal-card pr-modal-narrow" onClick={(e) => e.stopPropagation()}>
                        <div className="pr-modal-head">
                            <div className="pr-modal-title">{actionType === "income" ? "Приход" : "Списание"} — {actionItem.name}</div>
                            <button className="pr-modal-close" onClick={() => setActionItem(null)}><i className="fa-solid fa-xmark" /></button>
                        </div>
                        <div className="pr-modal-body">
                            <div className="pr-hint">В наличии: {actionItem.qty} {actionItem.unit}</div>
                            <div className="pr-field">
                                <label>Количество ({actionItem.unit})</label>
                                <input value={actionQty} onChange={(e) => setActionQty(e.target.value)} placeholder="0" />
                            </div>
                            <div className="pr-field">
                                <label>Комментарий</label>
                                <input value={actionNote} onChange={(e) => setActionNote(e.target.value)}
                                    placeholder={actionType === "income" ? "Например: Поступление от поставщика" : "Например: Выдано группе перед поездкой"} />
                            </div>
                            <button className={actionType === "income" ? "pr-btn-primary pr-full" : "pr-btn-danger pr-full"} onClick={submitAction}>
                                {actionType === "income" ? "Добавить приход" : "Провести списание"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showAddModal && (
                <div className="pr-modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="pr-modal-card pr-modal-narrow" onClick={(e) => e.stopPropagation()}>
                        <div className="pr-modal-head">
                            <div className="pr-modal-title">{addKind ? (addKind === "Запчасть" ? "Новая запчасть" : "Новый товар") : "Что добавляем?"}</div>
                            <button className="pr-modal-close" onClick={() => setShowAddModal(false)}><i className="fa-solid fa-xmark" /></button>
                        </div>
                        <div className="pr-modal-body">
                            {!addKind ? (
                                <div className="pr-type-choice-row">
                                    <button className="pr-type-choice-btn" onClick={() => setAddKind("Товар")}>
                                        <i className="fa-solid fa-box" /> Товар
                                    </button>
                                    <button className="pr-type-choice-btn" onClick={() => setAddKind("Запчасть")}>
                                        <i className="fa-solid fa-wrench" /> Запчасть
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="pr-field">
                                        <label>Наименование {addKind === "Запчасть" ? "запчасти" : "товара"}</label>
                                        <input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                                            placeholder={addKind === "Запчасть" ? "Например: Масляный фильтр" : "Например: Вода питьевая 1л"} />
                                    </div>
                                    {addKind === "Товар" && (
                                        <div className="pr-field">
                                            <label>Категория</label>
                                            {pickableCategories.length ? (
                                                <select value={addForm.category} onChange={(e) => setAddForm({ ...addForm, category: e.target.value })}>
                                                    {pickableCategories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                                                </select>
                                            ) : (
                                                <div className="pr-hint">Категорий пока нет — создайте их через 5 кликов на заголовок «Склад».</div>
                                            )}
                                        </div>
                                    )}
                                    <div className="pr-fgrid-two">
                                        <div className="pr-field">
                                            <label>Единица измерения</label>
                                            <input value={addForm.unit} onChange={(e) => setAddForm({ ...addForm, unit: e.target.value })} />
                                        </div>
                                        <div className="pr-field">
                                            <label>Начальный остаток</label>
                                            <input value={addForm.qty} onChange={(e) => setAddForm({ ...addForm, qty: e.target.value })} placeholder="0" />
                                        </div>
                                    </div>
                                    <button className="pr-btn-primary pr-full" onClick={submitAdd}>Добавить</button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showHistoryModal && (
                <div className="pr-modal-overlay" onClick={() => setShowHistoryModal(false)}>
                    <div className="pr-modal-card pr-modal-wide" onClick={(e) => e.stopPropagation()}>
                        <div className="pr-modal-head">
                            <div className="pr-modal-title">Общая история склада</div>
                            <button className="pr-modal-close" onClick={() => setShowHistoryModal(false)}><i className="fa-solid fa-xmark" /></button>
                        </div>
                        <div className="pr-modal-body">
                            {categoryNames.filter((c) => c !== "Все").map((cat) => {
                                const rows = [];
                                allItemsForHistory.filter((i) => i.category === cat).forEach((item) => {
                                    item.history.forEach((h) => rows.push({ ...h, itemName: item.name, unit: item.unit, isDeleted: item.isDeleted }));
                                });
                                rows.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
                                return (
                                    <div className="pr-history-section" key={cat}>
                                        <h4>{cat}</h4>
                                        <div className="pr-table-wrap">
                                            <table className="pr-table">
                                                <thead>
                                                    <tr><th>Дата</th><th>Товар</th><th>Операция</th><th>Кол-во</th><th>Комментарий</th></tr>
                                                </thead>
                                                <tbody>
                                                    {rows.length ? rows.map((h) => (
                                                        <tr key={h.id}>
                                                            <td>{h.date}</td>
                                                            <td>{h.itemName}{h.isDeleted && <span className="pr-deleted-tag"> (удалён)</span>}</td>
                                                            <td><span className={"pr-badge-status " + (h.type === "приход" ? "pr-ok" : "pr-bad")}>{h.type}</span></td>
                                                            <td className="pr-mono">{h.type === "приход" ? "+" : "−"}{h.qty} {h.unit}</td>
                                                            <td>{h.note}</td>
                                                        </tr>
                                                    )) : <tr><td colSpan={5} className="pr-hint-cell">История пуста</td></tr>}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Скрытая модалка: управление категориями и товарами (5 кликов на "Склад") ── */}
            {showCategoryModal && (
                <div className="pr-modal-overlay" onClick={() => setShowCategoryModal(false)}>
                    <div className="pr-modal-card pr-modal-narrow" onClick={(e) => e.stopPropagation()}>
                        <div className="pr-modal-head">
                            <div className="pr-modal-title">Категории и товары склада</div>
                            <button className="pr-modal-close" onClick={() => setShowCategoryModal(false)}><i className="fa-solid fa-xmark" /></button>
                        </div>
                        <div className="pr-modal-body">

                            {/* ── Категории ── */}
                            <div className="pr-section-label">Категории</div>

                            <div className="pr-category-list">
                                {categories.map((c) => (
                                    <div className="pr-category-row" key={c.id}>
                                        <span className="pr-category-name">
                                            {c.name}
                                            {Boolean(c.isProtected) && <span className="pr-category-lock"><i className="fa-solid fa-lock" /></span>}
                                        </span>
                                        <span className="pr-category-count">{c.itemsCount} тов.</span>
                                        {!c.isProtected && (
                                            <button className="pr-btn-mini pr-btn-mini-red" onClick={() => deleteCategory(c)}>
                                                <i className="fa-solid fa-trash" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                {!categories.length && <div className="pr-hint">Категорий пока нет.</div>}
                            </div>

                            <div className="pr-field" style={{ marginTop: 14 }}>
                                <label>Новая категория</label>
                                <input value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="Например: Вода" />
                            </div>
                            <button className="pr-btn-primary pr-full" onClick={submitAddCategory}>
                                <i className="fa-solid fa-plus" /> Добавить категорию
                            </button>

                            {/* ── Товары ── */}
                            <div className="pr-section-label" style={{ marginTop: 26 }}>Товары и запчасти</div>

                            <div className="pr-category-list">
                                {items.map((item) => (
                                    <div className="pr-category-row" key={item.id}>
                                        {editItemId === item.id ? (
                                            <>
                                                <input
                                                    className="pr-category-edit-input"
                                                    value={editItemName}
                                                    onChange={(e) => setEditItemName(e.target.value)}
                                                    autoFocus
                                                />
                                                <button className="pr-btn-mini" onClick={saveEditItem}><i className="fa-solid fa-check" /></button>
                                                <button className="pr-btn-mini" onClick={() => setEditItemId(null)}><i className="fa-solid fa-xmark" /></button>
                                            </>
                                        ) : (
                                            <>
                                                <span className="pr-category-name">{item.name}<span className="pr-category-lock" style={{ fontWeight: 400 }}>· {item.category}</span></span>
                                                <button className="pr-btn-mini" onClick={() => startEditItem(item)}><i className="fa-solid fa-pen" /></button>
                                                <button className="pr-btn-mini pr-btn-mini-red" onClick={() => deleteItem(item)}><i className="fa-solid fa-trash" /></button>
                                            </>
                                        )}
                                    </div>
                                ))}
                                {!items.length && <div className="pr-hint">Товаров пока нет.</div>}
                            </div>

                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Products;